import React from 'react';
import { TrendingUp, Zap, DollarSign, AlertTriangle } from 'lucide-react';

const UsageMonitoring = ({ userProfile }) => {
  // Mock current usage data - in production, this would come from API
  const currentUsage = {
    spending: 450, // Current month spending
    swaps: 6 // Current month swaps
  };

  const spendingLimit = userProfile?.spending_limit || 1000;
  const swapLimit = userProfile?.monthly_swap_limit || 10;

  const spendingPercentage = (currentUsage?.spending / spendingLimit) * 100;
  const swapPercentage = (currentUsage?.swaps / swapLimit) * 100;

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getWarningLevel = (percentage) => {
    if (percentage >= 90) return { show: true, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
    if (percentage >= 75) return { show: true, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
    return { show: false };
  };

  const spendingWarning = getWarningLevel(spendingPercentage);
  const swapWarning = getWarningLevel(swapPercentage);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Usage Monitoring
        </h2>
        <span className="text-sm text-gray-600">Current billing period</span>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Spending Usage */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-900">Spending Limit</span>
            </div>
            <span className="text-sm font-medium text-gray-600">
              ${currentUsage?.spending?.toLocaleString()} / ${spendingLimit?.toLocaleString()}
            </span>
          </div>
          
          <div className="relative">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getProgressColor(spendingPercentage)}`}
                style={{ width: `${Math.min(spendingPercentage, 100)}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {spendingPercentage?.toFixed(1)}% used
            </p>
          </div>

          {spendingWarning?.show && (
            <div className={`flex items-start gap-2 p-3 rounded-lg border ${spendingWarning?.bg} ${spendingWarning?.border}`}>
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${spendingWarning?.color}`} />
              <p className={`text-sm ${spendingWarning?.color}`}>
                {spendingPercentage >= 90
                  ? 'You\'re approaching your spending limit. Consider upgrading to Pro for higher limits.'
                  : 'You\'ve used over 75% of your spending limit this month.'}
              </p>
            </div>
          )}
        </div>

        {/* Swap Usage */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-gray-900">Monthly Swaps</span>
            </div>
            <span className="text-sm font-medium text-gray-600">
              {currentUsage?.swaps} / {swapLimit}
            </span>
          </div>
          
          <div className="relative">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getProgressColor(swapPercentage)}`}
                style={{ width: `${Math.min(swapPercentage, 100)}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {swapPercentage?.toFixed(1)}% used
            </p>
          </div>

          {swapWarning?.show && (
            <div className={`flex items-start gap-2 p-3 rounded-lg border ${swapWarning?.bg} ${swapWarning?.border}`}>
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${swapWarning?.color}`} />
              <p className={`text-sm ${swapWarning?.color}`}>
                {swapPercentage >= 90
                  ? 'You\'re running low on swaps. Upgrade to Pro for 100 swaps per month.'
                  : 'You\'ve used over 75% of your monthly swaps.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Additional Info */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid md:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-sm text-gray-600 mb-1">Transaction Fee</p>
            <p className="text-lg font-semibold text-gray-900">
              {userProfile?.tier === 'pro' ? '2.4%' : '2.9%'} + $0.30
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Swap Fee</p>
            <p className="text-lg font-semibold text-gray-900">
              {userProfile?.tier === 'pro' ? '1.0%' : '1.5%'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Resets On</p>
            <p className="text-lg font-semibold text-gray-900">
              {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)?.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageMonitoring;